import type { BannerItem } from "$root/components/banner-stack/types";
import { ActionText } from "@mdotm/shadcn/legacy/components";
import type { TFunction } from "i18next";
import { useState } from "react";
import type { Instrument } from "../../const";
import type { QuantityEditorBuilderProps } from "../builder";
import { describeSizingMessage, instrumentLabel } from "./sizingMessage";

/** How many sentences show before the list is expanded. */
const COLLAPSED_COUNT = 3;

function ResizedRowsList({
	sentences,
	t,
}: {
	sentences: Array<{ id: string; sentence: string }>;
	t: TFunction;
}): React.JSX.Element {
	const [expanded, setExpanded] = useState(false);
	const shown = expanded ? sentences : sentences.slice(0, COLLAPSED_COUNT);
	return (
		<div data-qualifier="CompositionEditor/ResizedRowsBanner">
			<ul className="list-disc pl-4">
				{shown.map(({ id, sentence }) => (
					<li key={id}>{sentence}</li>
				))}
			</ul>
			{sentences.length > COLLAPSED_COUNT && (
				<ActionText
					data-qualifier="CompositionEditor/ResizedRowsBanner/Toggle"
					onClick={() => setExpanded((open) => !open)}
				>
					{expanded
						? t("EDIT_COMPOSITION.SIZING_MESSAGE.BANNER.SHOW_LESS")
						: t("EDIT_COMPOSITION.SIZING_MESSAGE.BANNER.SHOW_ALL", { count: sentences.length })}
				</ActionText>
			)}
		</div>
	);
}

/**
 * The rows Cold War sized to another value than the one asked, each with why, except a typed edit,
 * which the builder already toasted. Derived from the rows' messages, so a row leaves the list once
 * it is sized again. Dismissing it lasts until the next
 * message lands: the id carries when the latest one did.
 */
export function resizedRowsBanner({
	instrumentBuilder,
	t,
}: {
	instrumentBuilder: Pick<QuantityEditorBuilderProps, "getComposition">;
	t: TFunction;
}): BannerItem | null {
	const resized = Array.from(instrumentBuilder.getComposition({ removeDeleted: true }).values()).flatMap(
		(row: Instrument) =>
			row.sizingMessage && !row.sizingMessage.toasted ? [{ row, sizingMessage: row.sizingMessage }] : [],
	);
	if (resized.length === 0) {
		return null;
	}
	const latest = resized.reduce(
		(last, { sizingMessage }) => (sizingMessage.receivedAt > last ? sizingMessage.receivedAt : last),
		"",
	);
	const sentences = resized.map(({ row, sizingMessage }) => ({
		id: row.id,
		sentence: describeSizingMessage(t, instrumentLabel(row), sizingMessage),
	}));
	return {
		id: `resized-rows-${latest}`,
		severity: "info",
		title: t("EDIT_COMPOSITION.SIZING_MESSAGE.BANNER.TITLE", { count: resized.length }),
		description: <ResizedRowsList sentences={sentences} t={t} />,
		dismissible: true,
	};
}
